import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Chat.css';
import { ArrowDownIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Virtuoso } from 'react-virtuoso';
import { sanitizeMessageHtml } from './sanitizeHtml';
import { graspApi, fetchMessageHistory, toWebSocketUrl } from './api';
import MessageMedia from './MessageMedia';
import MessageMicrolinks from './MessageMicrolinks';
import ChatComposer from './ChatComposer';
import ThemeToggle from './ThemeToggle';
import { capturePendingText, consumePendingText } from './pendingText';
import ChannelAvatar from './ChannelAvatar';
import AnswerDeck from './AnswerDeck';
import { ChatThreadSkeleton } from './Skeleton';
import TypingBubble from './TypingBubble';
import ConnectionStatus from './ConnectionStatus';
import { applyChatTheme, buildChatTheme, bubbleRadiusClass } from './chatTheme';
import './transitions.css';
import {
  buildTimelineItems,
  createLocalMessage,
  draftStorageKey,
  getActiveAnswerOptions,
  getActiveQuickReplies,
  mergeMessages,
  normalizeMessage,
  parseReminderContent,
  parseTimestamp,
  updateMessageStatus,
} from './messageUtils';
import ReminderPrompt from './push/ReminderPrompt';
import {
  canOfferPushOptIn,
  canShowPushDemoTools,
  formatReminderNudge,
  pushPromptDismissKey,
  readCachedPushEnabled,
  writeCachedPushEnabled,
} from './push/pushSupport';
import {
  disablePushReminders,
  requestTestPush,
  restorePushReminders,
  subscribeToPushReminders,
} from './push/subscribe';
import { maskEmail } from './auth/emailAuth';
import { useInstallPrompt } from './push/installPrompt';

const MAX_MESSAGE_LENGTH = 4000;
const WS_BASE_DELAY_MS = 1000;
const WS_MAX_DELAY_MS = 30000;
/** Move mid reconnect pill to the top banner after this wait */
const RECONNECT_ESCALATE_MS = 8000;
/** Stop auto-reconnect and require Retry after this wait */
const RECONNECT_CUTOFF_MS = 40000;
const PUSH_DEMO = canShowPushDemoTools();
/** The server must acknowledge the authenticated session after the socket opens. */
const WS_HANDSHAKE_TIMEOUT_MS = 10000;
/** A typing signal is only trusted for this long before it self-clears */
const TYPING_EXPIRY_MS = 7000;
/** How long to keep showing typing after the user sends, before giving up */
const AWAITING_REPLY_TIMEOUT_MS = 45000;
/**
 * After a bot message with no quick-replies, keep typing briefly — quiz flows
 * often send feedback first, then the next question a moment later.
 */
const INTER_PART_TYPING_MS = 6000;
/** Pause (with typing) between parts when several bot messages arrive together */
const MULTI_PART_GAP_MS = 1200;
/** Debounce layout-driven pins so image/quick-reply resize doesn't thrash */
const PIN_DEBOUNCE_MS = 50;

const ThreadEndSpacer = () => <div className="chat-thread-end-spacer" />;

/**
 * Keep item width full so user bubbles can margin-left:auto.
 * Do NOT set display:flex on List — that breaks Virtuoso's scroll metrics.
 */
const VirtuosoItem = React.forwardRef(function VirtuosoItem(
  { style, children, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      {...props}
      style={{
        ...style,
        width: '100%',
      }}
    >
      {children}
    </div>
  );
});

/* Message status glyphs. Inline rather than from heroicons so they can be sized
   to the 12px meta line without fighting utility classes. */
const ClockIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);
const CheckIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);
const AlertIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
    strokeLinecap="round" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5" />
    <path d="M12 16.4v.1" />
  </svg>
);

const MESSAGE_ENTER_MS = 340;
/** How far from the end still counts as "at bottom" */
const AT_BOTTOM_THRESHOLD_PX = 48;
/** How long pinToBottom treats scroll events as programmatic */
const PROGRAMMATIC_SCROLL_MS = 120;

function messageKey(msg) {
  if (!msg) return null;
  if (msg.id != null) return `id:${msg.id}`;
  if (msg.localId) return `local:${msg.localId}`;
  return null;
}

function Chat() {
  const { id, channel } = useParams();
  const { token, username, logout } = useAuth();
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [webChatChannel, setWebChatChannel] = useState(null);
  const [channelReady, setChannelReady] = useState(false);
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadWhileAway, setUnreadWhileAway] = useState(0);
  const [sendError, setSendError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  // 'deck' shows the answer options as the primary input; 'text' swaps in the
  // composer. With no quick replies there is nothing to choose, so the composer
  // is always shown regardless of this value.
  const [composerMode, setComposerMode] = useState('deck');
  const [connectionPhase, setConnectionPhase] = useState('bootstrapping'); // bootstrapping | mid | top | failed | ready
  const [reconnectCycle, setReconnectCycle] = useState(0);
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [reminderStatusReady, setReminderStatusReady] = useState(false);
  const [showReminderPrompt, setShowReminderPrompt] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [pushNudge, setPushNudge] = useState(null);

  const socketRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const handshakeTimerRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const autoReconnectRef = useRef(true);
  const connectWebSocketRef = useRef(null);
  const virtuosoRef = useRef(null);
  const threadRef = useRef(null);
  const atBottomRef = useRef(true);
  const stickToBottomRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const programmaticResetRef = useRef(null);
  const isScrollingRef = useRef(false);
  const typingExpiryRef = useRef(null);
  const pinTimerRef = useRef(null);
  const pinFrameRef = useRef(null);
  const seenMessageKeysRef = useRef(new Set());
  const historySeededRef = useRef(false);
  const botRevealTimerRef = useRef(null);
  const botRevealTokenRef = useRef(0);
  const inputRef = useRef(null);
  const userSentMessageRef = useRef(false);
  const reminderPromptShownRef = useRef(false);
  const navigate = useNavigate();
  const { canInstall, promptInstall } = useInstallPrompt();

  /** User started scrolling up — cancel pending pins so they don't yank back */
  const releaseStickFromUserRef = useRef(() => {});
  releaseStickFromUserRef.current = () => {
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
    }
    if (pinTimerRef.current) {
      clearTimeout(pinTimerRef.current);
      pinTimerRef.current = null;
    }
    if (pinFrameRef.current) {
      cancelAnimationFrame(pinFrameRef.current);
      pinFrameRef.current = null;
    }
    if (programmaticResetRef.current) {
      clearTimeout(programmaticResetRef.current);
      programmaticResetRef.current = null;
    }
    stickToBottomRef.current = false;
    setShowScrollButton(true);
  };

  // Stable Virtuoso chrome — remounting Scroller/Item resets scroll position
  const virtuosoComponents = useMemo(
    () => ({
      Item: VirtuosoItem,
      Footer: ThreadEndSpacer,
      Scroller: React.forwardRef(function ChatScroller(
        { style, children, ...props },
        ref
      ) {
        return (
          <div
            ref={ref}
            {...props}
            style={style}
            onWheel={(event) => {
              props.onWheel?.(event);
              // deltaY < 0 = fingers/wheel scrolling toward older messages
              if (event.deltaY < 0) {
                releaseStickFromUserRef.current();
              }
            }}
            onTouchMove={(event) => {
              props.onTouchMove?.(event);
              if (!atBottomRef.current) {
                releaseStickFromUserRef.current();
              }
            }}
          >
            {children}
          </div>
        );
      }),
    }),
    []
  );

  const [enteringKeys, setEnteringKeys] = useState(() => new Set());

  const theme = useMemo(() => buildChatTheme(webChatChannel), [webChatChannel]);
  const timelineItems = useMemo(() => buildTimelineItems(messages), [messages]);

  // Campaign links: stash ?text= from the URL before anything else strips it.
  useEffect(() => {
    capturePendingText(id, channel, window.location.search);
  }, [id, channel]);

  // Brand tokens go on :root, not the shell: the page backdrop sits outside
  // .chat-shell, and the video theater portals into document.body, so anything
  // scoped to the shell leaves both unthemed.
  useEffect(() => {
    applyChatTheme(theme);
  }, [theme]);

  // Publish the dock's real height so the scroll-to-bottom button can anchor to
  // it. The old stylesheet hand-measured four :has() offsets, all of which went
  // stale the moment the dock changed size.
  const bottomRef = useRef(null);
  useEffect(() => {
    const el = bottomRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => {
      document.documentElement.style.setProperty(
        '--chat-bottom-height',
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // iOS Safari overlays the software keyboard on top of a 100dvh element rather
  // than resizing it, which hides the composer. visualViewport is the only
  // reliable way to learn how much is covered.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const publish = () => {
      const inset = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop
      );
      document.documentElement.style.setProperty(
        '--chat-keyboard-inset',
        `${Math.round(inset)}px`
      );
    };
    publish();
    vv.addEventListener('resize', publish);
    vv.addEventListener('scroll', publish);
    return () => {
      vv.removeEventListener('resize', publish);
      vv.removeEventListener('scroll', publish);
      document.documentElement.style.setProperty('--chat-keyboard-inset', '0px');
    };
  }, []);

  // The menu had no way to dismiss itself — no outside click, no Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!event.target.closest?.('.chat-header-actions')) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const profilePictureUrl = webChatChannel?.profile_picture_url || '';

  const draftKey = draftStorageKey({
    channelId: id,
    channelName: channel,
    username,
  });

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearHandshakeTimer = useCallback(() => {
    if (handshakeTimerRef.current) {
      clearTimeout(handshakeTimerRef.current);
      handshakeTimerRef.current = null;
    }
  }, []);

  const clearPendingPins = useCallback(() => {
    if (pinTimerRef.current) {
      clearTimeout(pinTimerRef.current);
      pinTimerRef.current = null;
    }
    if (pinFrameRef.current) {
      cancelAnimationFrame(pinFrameRef.current);
      pinFrameRef.current = null;
    }
    if (programmaticResetRef.current) {
      clearTimeout(programmaticResetRef.current);
      programmaticResetRef.current = null;
    }
  }, []);

  /**
   * Single scroll driver. Scrolls past the footer spacer (rather than aligning
   * the last item) so new content always clears the actions bar.
   */
  const pinToBottom = useCallback(() => {
    stickToBottomRef.current = true;
    atBottomRef.current = true;
    programmaticScrollRef.current = true;
    setShowScrollButton(false);
    setUnreadWhileAway(0);

    clearPendingPins();
    pinTimerRef.current = setTimeout(() => {
      pinTimerRef.current = null;
      pinFrameRef.current = requestAnimationFrame(() => {
        pinFrameRef.current = null;
        virtuosoRef.current?.scrollTo({ top: Number.MAX_SAFE_INTEGER });
        programmaticResetRef.current = setTimeout(() => {
          programmaticResetRef.current = null;
          programmaticScrollRef.current = false;
        }, PROGRAMMATIC_SCROLL_MS);
      });
    }, PIN_DEBOUNCE_MS);
  }, [clearPendingPins]);

  const isFollowingBottom = useCallback(() => stickToBottomRef.current, []);

  const stopTyping = useCallback(() => {
    if (typingExpiryRef.current) {
      clearTimeout(typingExpiryRef.current);
      typingExpiryRef.current = null;
    }
    setIsBotTyping(false);
  }, []);

  /** Typing always carries its own expiry so a stray signal can never stick. */
  const startTyping = useCallback((ttlMs = TYPING_EXPIRY_MS) => {
    setIsBotTyping(true);
    if (typingExpiryRef.current) clearTimeout(typingExpiryRef.current);
    typingExpiryRef.current = setTimeout(() => {
      typingExpiryRef.current = null;
      setIsBotTyping(false);
    }, ttlMs);
  }, []);

  const clearBotReveal = useCallback(() => {
    botRevealTokenRef.current += 1;
    if (botRevealTimerRef.current) {
      clearTimeout(botRevealTimerRef.current);
      botRevealTimerRef.current = null;
    }
  }, []);

  const mergeIncomingIntoState = useCallback(
    (msgs) => {
      setMessages((prev) => {
        const before = prev.length;
        const merged = mergeMessages(prev, msgs);
        const added = merged.length - before;
        if (added > 0 && !isFollowingBottom()) {
          setUnreadWhileAway((count) => count + added);
        }
        return merged;
      });
    },
    [isFollowingBottom]
  );

  /**
   * Decide whether this bot turn looks finished (has action buttons) or more
   * parts may still arrive (feedback → next quiz question).
   */
  const settleBotTyping = useCallback(
    (botMessages) => {
      const latestWithReplies = [...botMessages]
        .reverse()
        .find((msg) => msg.quick_replies.length > 0);
      if (latestWithReplies) {
        setQuickReplies(latestWithReplies.quick_replies);
      }

      const last = botMessages[botMessages.length - 1];
      if (last?.quick_replies?.length > 0) {
        stopTyping();
      } else {
        startTyping(INTER_PART_TYPING_MS);
      }
      if (isFollowingBottom()) pinToBottom();
    },
    [stopTyping, startTyping, isFollowingBottom, pinToBottom]
  );

  const fetchAndMergeHistory = useCallback(async () => {
    if (!token || !username || !channelReady) return;
    setIsLoadingHistory(true);
    try {
      const fetched = await fetchMessageHistory({
        channelAddress: channel,
        channelId: id,
      });
      setMessages((prev) => mergeMessages(prev, fetched));

      // History can land after a live reply (reconnect); only let it set actions
      // when it actually has them, or when the user demonstrably replied last.
      const fromHistory = getActiveQuickReplies(fetched);
      const lastConversation = [...fetched]
        .reverse()
        .find((msg) => msg.kind !== 'reminder');
      if (fromHistory.length > 0) {
        setQuickReplies(fromHistory);
      } else if (lastConversation?.is_from_user) {
        setQuickReplies([]);
      }
    } catch (error) {
      console.error('Failed to fetch message history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [token, username, channelReady, channel, id]);

  useEffect(() => {
    setMessages([]);
    setChannelReady(false);
    setIsSocketReady(false);
    setUnreadWhileAway(0);
    setQuickReplies([]);
    setIsLoadingHistory(false);
    reconnectAttemptRef.current = 0;
    autoReconnectRef.current = true;
    stickToBottomRef.current = true;
    seenMessageKeysRef.current = new Set();
    historySeededRef.current = false;
    setEnteringKeys(new Set());
    clearBotReveal();
    stopTyping();
    clearPendingPins();
    setConnectionPhase('bootstrapping');
    clearReconnectTimer();
    clearHandshakeTimer();
  }, [id, channel, clearReconnectTimer, clearHandshakeTimer, clearPendingPins, clearBotReveal, stopTyping]);

  useEffect(() => {
    userSentMessageRef.current = false;
    reminderPromptShownRef.current = false;
    setShowReminderPrompt(false);
    setReminderError('');
    setReminderStatusReady(false);
    const cached = Boolean(username) && readCachedPushEnabled(username, id, channel);
    setRemindersEnabled(cached);
  }, [id, channel, username]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onMessage = (event) => {
      const data = event.data || {};
      if (
        data.type === 'webchat-push-received' ||
        data.type === 'webchat-push-retained' ||
        data.type === 'webchat-push-displayed'
      ) {
        setPushNudge(formatReminderNudge(data));
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!pushNudge) return undefined;
    const timer = setTimeout(() => setPushNudge(null), 16000);
    return () => clearTimeout(timer);
  }, [pushNudge]);

  useEffect(() => {
    if (!token || !username || !canOfferPushOptIn()) {
      setReminderStatusReady(true);
      return undefined;
    }

    let cancelled = false;
    restorePushReminders(id, channel)
      .then((result) => {
        if (cancelled) return;
        const enabled = Boolean(result?.enabled);
        setRemindersEnabled(enabled);
        writeCachedPushEnabled(username, id, channel, enabled);
        setReminderStatusReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        const cached = readCachedPushEnabled(username, id, channel);
        setRemindersEnabled(cached);
        setReminderStatusReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [token, username, id, channel]);

  const maybeOfferReminderPrompt = useCallback(() => {
    if (
      !canOfferPushOptIn() ||
      !reminderStatusReady ||
      remindersEnabled ||
      reminderPromptShownRef.current
    ) {
      return;
    }

    try {
      if (sessionStorage.getItem(pushPromptDismissKey(id, channel)) === '1') {
        reminderPromptShownRef.current = true;
        return;
      }
    } catch {
      /* ignore */
    }

    reminderPromptShownRef.current = true;
    setShowReminderPrompt(true);
    setReminderError('');
  }, [id, channel, remindersEnabled, reminderStatusReady]);

  const handleDismissReminderPrompt = useCallback(() => {
    setShowReminderPrompt(false);
    try {
      sessionStorage.setItem(pushPromptDismissKey(id, channel), '1');
    } catch {
      /* ignore */
    }
  }, [id, channel]);

  const sendTestReminder = useCallback(async () => {
    const testResult = await requestTestPush(id, channel);
    if (!testResult?.sent) {
      setPushNudge({
        title: 'We could not send a reminder just now.',
        body: 'Please try again in a moment.',
      });
      return testResult;
    }

    setPushNudge(formatReminderNudge());
    return testResult;
  }, [id, channel]);

  const handleEnableReminders = useCallback(async () => {
    setMenuOpen(false);
    setReminderBusy(true);
    setReminderError('');
    try {
      await subscribeToPushReminders(id, channel);
      setRemindersEnabled(true);
      setReminderStatusReady(true);
      writeCachedPushEnabled(username, id, channel, true);
      setShowReminderPrompt(false);
      try {
        sessionStorage.removeItem(pushPromptDismissKey(id, channel));
      } catch {
        /* ignore */
      }
    } catch (error) {
      setReminderError(
        error?.message || 'We could not enable reminders. Please try again.'
      );
      setShowReminderPrompt(true);
    } finally {
      setReminderBusy(false);
    }
  }, [id, channel, username]);

  const handleSendTestReminder = useCallback(async () => {
    setMenuOpen(false);
    setReminderBusy(true);
    setReminderError('');
    try {
      await sendTestReminder();
    } catch (error) {
      setPushNudge({
        title: 'We could not send a reminder just now.',
        body: error?.message || 'Please try again in a moment.',
      });
    } finally {
      setReminderBusy(false);
    }
  }, [sendTestReminder]);

  const handleDisableReminders = useCallback(async () => {
    setMenuOpen(false);
    setReminderBusy(true);
    setReminderError('');
    try {
      await disablePushReminders(id, channel);
      setRemindersEnabled(false);
      writeCachedPushEnabled(username, id, channel, false);
      setShowReminderPrompt(false);
    } catch (error) {
      setReminderError(
        error?.message || 'We could not turn off reminders. Please try again.'
      );
    } finally {
      setReminderBusy(false);
    }
  }, [id, channel, username]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(draftKey);
      if (saved != null) setInputMessage(saved);
    } catch {
      /* ignore */
    }
  }, [draftKey]);

  useEffect(() => {
    try {
      if (inputMessage) sessionStorage.setItem(draftKey, inputMessage);
      else sessionStorage.removeItem(draftKey);
    } catch {
      /* ignore */
    }
  }, [inputMessage, draftKey]);

  useEffect(() => {
    const fetchChannelInfo = async () => {
      try {
        const response = await graspApi.get(
          `/v2/claim/channel/webchat/${id}/${channel}/`
        );
        setWebChatChannel(response.data);
      } catch (error) {
        console.error('Failed to fetch WebChatChannel:', error);
      }
    };
    if (username) fetchChannelInfo();
  }, [username, id, channel]);

  // The channel only has to exist. Which RapidPro channel it is, and on which
  // instance, is the backend's own lookup; nothing about it travels on the socket.
  useEffect(() => {
    let cancelled = false;
    const checkChannel = async () => {
      try {
        await graspApi.get(`/v2/channels/${id}/${channel}/`);
        if (!cancelled) setChannelReady(true);
      } catch (err) {
        console.error('Failed to resolve the channel:', err);
      }
    };
    checkChannel();
    return () => {
      cancelled = true;
    };
  }, [id, channel]);

  const appendIncomingMessages = useCallback(
    (incoming) => {
      const list = Array.isArray(incoming) ? incoming : [incoming];
      const normalized = list
        .map((msg) => normalizeMessage(msg))
        .filter(Boolean);
      if (normalized.length === 0) return;

      const reminderMessages = normalized.filter(
        (msg) => msg.kind === 'reminder'
      );
      const botMessages = normalized.filter(
        (msg) => !msg.is_from_user && msg.kind !== 'reminder'
      );
      const otherMessages = normalized.filter((msg) => msg.is_from_user);

      if (otherMessages.length > 0) {
        mergeIncomingIntoState(otherMessages);
      }

      if (reminderMessages.length > 0) {
        mergeIncomingIntoState(reminderMessages);
        if (isFollowingBottom()) pinToBottom();
      }

      if (botMessages.length === 0) return;

      if (userSentMessageRef.current) {
        maybeOfferReminderPrompt();
      }

      clearBotReveal();

      // One part: show it, then keep typing if more of the turn may follow
      if (botMessages.length === 1) {
        mergeIncomingIntoState(botMessages);
        settleBotTyping(botMessages);
        return;
      }

      // Several parts at once (common for quiz feedback + next question):
      // reveal with a short typing beat between each bubble.
      const token = botRevealTokenRef.current;
      let index = 0;

      const revealNext = () => {
        if (token !== botRevealTokenRef.current) return;

        const msg = botMessages[index];
        mergeIncomingIntoState([msg]);
        if (msg.quick_replies.length > 0) {
          setQuickReplies(msg.quick_replies);
        }
        if (isFollowingBottom()) pinToBottom();

        index += 1;
        if (index >= botMessages.length) {
          settleBotTyping(botMessages);
          return;
        }

        startTyping(MULTI_PART_GAP_MS + 800);
        botRevealTimerRef.current = setTimeout(revealNext, MULTI_PART_GAP_MS);
      };

      revealNext();
    },
    [
      mergeIncomingIntoState,
      clearBotReveal,
      settleBotTyping,
      isFollowingBottom,
      pinToBottom,
      startTyping,
      maybeOfferReminderPrompt,
    ]
  );

  const connectWebSocket = useCallback(() => {
    if (!channelReady || !token) return;

    clearReconnectTimer();

    if (socketRef.current) {
      const previous = socketRef.current;
      previous.onopen = null;
      previous.onmessage = null;
      previous.onerror = null;
      previous.onclose = null;
      previous.close();
      socketRef.current = null;
    }

    const socket = new WebSocket(
      toWebSocketUrl(process.env.REACT_APP_API_WEBSOCKET_URL)
    );
    socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        autoReconnectRef.current = true;
      socket.send(
        JSON.stringify({
          token,
          web_channel_id: id,
          web_channel_name: channel,
        })
      );
      clearHandshakeTimer();
      handshakeTimerRef.current = setTimeout(() => {
        handshakeTimerRef.current = null;
        if (socketRef.current !== socket || socket.readyState !== WebSocket.OPEN) return;
        console.error('WebSocket handshake timed out');
        socket.close(4000, 'Handshake timeout');
      }, WS_HANDSHAKE_TIMEOUT_MS);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'ping') {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'pong' }));
          }
          return;
        }

        if (
          data.type === 'pong' ||
          data.type === 'typing'
        ) {
          if (data.type === 'typing') {
            startTyping();
            if (isFollowingBottom()) pinToBottom();
          }
          return;
        }

        if (data.type === 'connection_established') {
          clearHandshakeTimer();
          setIsSocketReady(true);
          setConnectionPhase('ready');
          fetchAndMergeHistory();
          return;
        }

        if (data.error) {
          console.error('WebSocket error payload:', data.error);
          clearHandshakeTimer();
          setIsSocketReady(false);
          if (/authentication|not authenticated/i.test(data.error)) {
            autoReconnectRef.current = false;
            clearReconnectTimer();
            setConnectionPhase('failed');
            logout();
          }
          return;
        }

        if (Array.isArray(data.messages)) {
          appendIncomingMessages(data.messages);
          return;
        }

        const incoming = normalizeMessage(data);
        if (!incoming) return;
        appendIncomingMessages([incoming]);
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    socket.onerror = () => {
      clearHandshakeTimer();
      setIsSocketReady(false);
    };

    socket.onclose = (event) => {
      clearHandshakeTimer();
      setIsSocketReady(false);
      if (socketRef.current === socket) socketRef.current = null;
      if (event.code === 4001) {
        autoReconnectRef.current = false;
        clearReconnectTimer();
        setConnectionPhase('failed');
        logout();
        return;
      }
      if (!shouldReconnectRef.current) return;
      if (!autoReconnectRef.current) return;

      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(WS_MAX_DELAY_MS, WS_BASE_DELAY_MS * 2 ** attempt);
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(() => {
        connectWebSocketRef.current?.();
      }, delay);
    };
  }, [
    token,
    channelReady,
    id,
    channel,
    clearReconnectTimer,
    clearHandshakeTimer,
    fetchAndMergeHistory,
    appendIncomingMessages,
    pinToBottom,
    isFollowingBottom,
    startTyping,
    logout,
  ]);

  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  useEffect(() => {
    if (!channelReady || !token) {
      setConnectionPhase('bootstrapping');
      autoReconnectRef.current = false;
      clearReconnectTimer();
      clearHandshakeTimer();
      return undefined;
    }

    if (isSocketReady) {
      setConnectionPhase('ready');
      autoReconnectRef.current = true;
      return undefined;
    }

    setConnectionPhase('mid');
    autoReconnectRef.current = true;

    const escalateTimer = setTimeout(() => {
      setConnectionPhase((prev) => (prev === 'ready' ? prev : 'top'));
    }, RECONNECT_ESCALATE_MS);

    const cutoffTimer = setTimeout(() => {
      autoReconnectRef.current = false;
      clearReconnectTimer();
      setConnectionPhase((prev) => (prev === 'ready' ? prev : 'failed'));
    }, RECONNECT_CUTOFF_MS);

    return () => {
      clearTimeout(escalateTimer);
      clearTimeout(cutoffTimer);
    };
  }, [channelReady, token, isSocketReady, reconnectCycle, clearReconnectTimer, clearHandshakeTimer]);

  const handleRetryConnection = useCallback(() => {
    autoReconnectRef.current = true;
    reconnectAttemptRef.current = 0;
    setConnectionPhase('mid');
    setReconnectCycle((cycle) => cycle + 1);
    clearReconnectTimer();
    connectWebSocket();
  }, [clearReconnectTimer, connectWebSocket]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    autoReconnectRef.current = true;
    if (channelReady && token) connectWebSocket();
    return () => {
      shouldReconnectRef.current = false;
      clearReconnectTimer();
      clearHandshakeTimer();
      if (socketRef.current) {
        const socket = socketRef.current;
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
        socketRef.current = null;
      }
      setIsSocketReady(false);
    };
  }, [channelReady, token, connectWebSocket, clearReconnectTimer, clearHandshakeTimer]);

  useEffect(() => {
    if (channelReady) fetchAndMergeHistory();
  }, [channelReady, fetchAndMergeHistory]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isLoadingHistory) {
      historySeededRef.current = false;
      return undefined;
    }

    // First paint after history: mark everything as already seen (no animation)
    if (!historySeededRef.current) {
      messages.forEach((msg) => {
        const key = messageKey(msg);
        if (key) seenMessageKeysRef.current.add(key);
      });
      historySeededRef.current = true;
      return undefined;
    }

    const fresh = [];
    messages.forEach((msg) => {
      const key = messageKey(msg);
      if (!key || seenMessageKeysRef.current.has(key)) return;
      seenMessageKeysRef.current.add(key);

      // Server echo of an optimistic send — already slid in via localId
      if (msg.is_from_user && msg.id != null) return;

      fresh.push(key);
    });

    if (fresh.length === 0) return undefined;

    setEnteringKeys((prev) => {
      const next = new Set(prev);
      fresh.forEach((key) => next.add(key));
      return next;
    });

    const timers = fresh.map((key) =>
      window.setTimeout(() => {
        setEnteringKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, MESSAGE_ENTER_MS)
    );

    return () => timers.forEach((id) => clearTimeout(id));
  }, [messages, isLoadingHistory]);

  const showWaitingBubble = isBotTyping;

  useEffect(() => () => {
    if (typingExpiryRef.current) clearTimeout(typingExpiryRef.current);
    if (botRevealTimerRef.current) clearTimeout(botRevealTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isLoadingHistory && messages.length > 0) {
      pinToBottom();
    }
  }, [isLoadingHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // The dock reserves space in the layout — re-pin once when it toggles
  useEffect(() => {
    if (isFollowingBottom()) pinToBottom();
  }, [showWaitingBubble, pinToBottom, isFollowingBottom]);

  const handleMediaLayout = useCallback(() => {
    if (isFollowingBottom()) pinToBottom();
  }, [pinToBottom, isFollowingBottom]);

  const sendText = useCallback(
    (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed) return false;
      if (trimmed.length > MAX_MESSAGE_LENGTH) {
        setSendError(`Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`);
        return false;
      }
      setSendError('');
      setQuickReplies([]);
      setComposerMode('deck');
      stickToBottomRef.current = true;
      clearBotReveal();

      const userMessage = createLocalMessage({
        username,
        content: trimmed,
        is_from_user: true,
        status: 'pending',
      });
      setMessages((prev) => mergeMessages(prev, [userMessage]));

      const socketOpen =
        isSocketReady && socketRef.current?.readyState === WebSocket.OPEN;

      if (!socketOpen) {
        setMessages((prev) =>
          updateMessageStatus(prev, userMessage.localId, 'failed')
        );
        // Clear the field even on failure: the text now lives in the failed
        // bubble, and leaving a copy behind lets a second tap duplicate it.
        setInputMessage('');
        return true;
      }

      try {
        socketRef.current.send(JSON.stringify({ text: trimmed }));
        setMessages((prev) =>
          updateMessageStatus(prev, userMessage.localId, 'sent')
        );
        userSentMessageRef.current = true;
        setInputMessage('');
        startTyping(AWAITING_REPLY_TIMEOUT_MS);
        try {
          sessionStorage.removeItem(draftKey);
        } catch {
          /* ignore */
        }
        pinToBottom();
      } catch (error) {
        console.error('Error sending message via WebSocket:', error);
        stopTyping();
        setMessages((prev) =>
          updateMessageStatus(prev, userMessage.localId, 'failed')
        );
      }
      return true;
    },
    [username, isSocketReady, draftKey, pinToBottom, startTyping, stopTyping, clearBotReveal]
  );

  const handleSendMessage = (e) => {
    e?.preventDefault?.();
    sendText(inputMessage);
  };

  const handleQuickReplyClick = (reply) => {
    sendText(reply);
  };

  const handleRetryMessage = (msg) => {
    if (!msg?.content) return;
    // Re-send first, then drop the old row. sendText can bail (empty after
    // trimming, over the length cap) and removing first would lose the text.
    if (sendText(msg.content)) {
      setMessages((prev) => prev.filter((m) => m.localId !== msg.localId));
    }
  };

  // Deck options: real quick replies, else numbered lines extracted from the
  // active bot turn (quiz flows send options as text, not quick_replies).
  const answerOptions = useMemo(
    () => getActiveAnswerOptions(messages, quickReplies),
    [messages, quickReplies]
  );

  const pendingTextSentRef = useRef(false);
  useEffect(() => {
    if (!isSocketReady || pendingTextSentRef.current) return;
    const text = consumePendingText(id, channel);
    if (!text) return;
    pendingTextSentRef.current = true;
    sendText(text);
  }, [isSocketReady, id, channel, sendText]);

  const showAnswerDeck = answerOptions.length > 0 && composerMode === 'deck';

  const handleTypeInstead = () => {
    setComposerMode('text');
    // Wait for the composer to mount before focusing it.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleScrollToBottom = () => {
    pinToBottom();
  };

  const handleEndChat = async () => {
    setMenuOpen(false);
    await logout();
    navigate(`/${id}/${channel}`);
  };

  const handleNotYou = async () => {
    setMenuOpen(false);
    await logout();
    navigate(`/${id}/${channel}`);
  };

  const quickReplyKey = quickReplies.join('|');

  // Quick replies grow the actions bar — re-pin once so content still clears it
  useEffect(() => {
    if (!quickReplyKey) return;
    if (isFollowingBottom()) pinToBottom();
  }, [quickReplyKey, pinToBottom, isFollowingBottom]);

  const renderTimelineItem = (index, item) => {
    if (item.type === 'separator') {
      return (
        <div className="chat-day-separator">
          <span>{item.label}</span>
        </div>
      );
    }

    const msg = item.message;
    const messageIndex = messages.findIndex(
      (m) =>
        (m.id != null && m.id === msg.id) ||
        (m.localId && m.localId === msg.localId)
    );
    const previousMsg = messageIndex > 0 ? messages[messageIndex - 1] : null;
    const nextMsg =
      messageIndex >= 0 && messageIndex < messages.length - 1
        ? messages[messageIndex + 1]
        : null;
    const isReminder = msg.kind === 'reminder';
    const isFirstInGroup =
      isReminder ||
      !previousMsg ||
      previousMsg.is_from_user !== msg.is_from_user ||
      previousMsg.kind === 'reminder';
    const isLastInGroup =
      isReminder ||
      !nextMsg ||
      nextMsg.is_from_user !== msg.is_from_user ||
      nextMsg.kind === 'reminder';
    const safeContent = msg.content ? sanitizeMessageHtml(msg.content) : '';
    // Image and video media bleed to the bubble edges, WhatsApp-style.
    // Documents and audio keep the padded layout.
    const hasImageBleed =
      !!msg.image && /\.(jpe?g|png|gif|webp|mp4|webm|mov)(\?|$)/i.test(msg.image);
    const radius = bubbleRadiusClass(
      msg.is_from_user,
      isFirstInGroup,
      isLastInGroup
    );
    const key = messageKey(msg);
    const isEntering = key != null && enteringKeys.has(key);
    const reminderCopy = isReminder ? parseReminderContent(msg.content) : null;

    if (isReminder) {
      return (
        <div className={`chat-bubble-row reminder${isEntering ? ' is-entering' : ''}`}>
          <div className="chat-reminder-note" role="note">
            <span className="chat-reminder-note-kicker">Reminder</span>
            {reminderCopy.title ? (
              <p className="chat-reminder-note-title">{reminderCopy.title}</p>
            ) : null}
            {reminderCopy.body ? (
              <p className="chat-reminder-note-body">{reminderCopy.body}</p>
            ) : null}
          </div>
        </div>
      );
    }

    return (
        <div
        className={`chat-bubble-row ${msg.is_from_user ? 'user' : 'bot'}${isEntering ? ' is-entering' : ''}${isFirstInGroup ? ' is-group-start' : ' is-group-follow'}`}
      >
        <div
          className={`chat-bubble ${msg.is_from_user ? 'user' : 'bot'}${hasImageBleed ? ' has-media' : ''} ${radius}`}
        >
          {msg.image && (
            <MessageMedia fileUrl={msg.image} onLoad={handleMediaLayout} />
          )}
          {safeContent && (
            <div className="chat-bubble-text">
              <span dangerouslySetInnerHTML={{ __html: safeContent }} />
            </div>
          )}
          {safeContent && <MessageMicrolinks html={safeContent} />}
          {isLastInGroup && (
            <div className="chat-bubble-meta">
              {msg.timestamp && (
                <span>
                  {parseTimestamp(msg.timestamp)
                    .toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })
                    .replace(/(am|pm)/i, (match) => match.toUpperCase())}
                </span>
              )}
              {/* Only pending/sent/failed exist — the backend carries no
                  delivery or read state, so there is no second tick to draw. */}
              {msg.is_from_user && msg.status === 'pending' && (
                <span className="chat-status chat-status--pending" title="Sending">
                  <ClockIcon aria-hidden="true" />
                  <span className="sr-only">Sending</span>
                </span>
              )}
              {msg.is_from_user && msg.status === 'sent' && (
                <span className="chat-status chat-status--sent" title="Sent">
                  <CheckIcon aria-hidden="true" />
                  <span className="sr-only">Sent</span>
                </span>
              )}
              {msg.is_from_user && msg.status === 'failed' && (
                <button
                  type="button"
                  className="chat-status-btn"
                  onClick={() => handleRetryMessage(msg)}
                >
                  <AlertIcon aria-hidden="true" />
                  Not sent · Retry
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="chat-app">
      <div className="chat-shell">
        <header className="chat-header">
          <div className="chat-header-identity">
            <ChannelAvatar
              src={profilePictureUrl}
              name={webChatChannel?.channel_name || channel}
              zoomable
            />
            <div className="chat-header-text">
              <span className="chat-header-title">
                {webChatChannel?.channel_name || channel || 'Chat'}
              </span>
              <span className="chat-header-subtitle">
                {connectionPhase === 'ready' && username
                  ? maskEmail(username)
                  : connectionPhase === 'bootstrapping'
                    ? 'Connecting'
                    : connectionPhase === 'ready'
                      ? 'Chat'
                      : connectionPhase === 'failed'
                        ? 'Offline'
                        : 'Reconnecting'}
                {connectionPhase !== 'ready' &&
                  connectionPhase !== 'bootstrapping' &&
                  connectionPhase !== 'failed' && (
                    <span className="connection-ellipsis connection-ellipsis--header">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  )}
              </span>
            </div>
          </div>
          <div className="chat-header-actions">
            <ThemeToggle />
            <button
              type="button"
              className="chat-header-menu"
              aria-label="Chat menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
            {menuOpen && (
              <div className="chat-header-dropdown" role="menu">
                {canOfferPushOptIn() && remindersEnabled ? (
                  <>
                    {PUSH_DEMO ? (
                      <button type="button" onClick={handleSendTestReminder} disabled={reminderBusy}>
                        {reminderBusy ? 'Sending…' : 'Send test reminder'}
                      </button>
                    ) : null}
                    <button type="button" onClick={handleDisableReminders} disabled={reminderBusy}>
                      Turn off reminders
                    </button>
                  </>
                ) : null}
                {canOfferPushOptIn() && !remindersEnabled ? (
                  <button type="button" onClick={handleEnableReminders} disabled={reminderBusy}>
                    {reminderBusy ? 'Enabling…' : 'Enable reminders'}
                  </button>
                ) : null}
                {canInstall ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      promptInstall();
                    }}
                  >
                    Add to Home Screen
                  </button>
                ) : null}
                <button type="button" onClick={handleNotYou}>
                  Not you?
                </button>
                <button type="button" className="chat-header-dropdown-danger" onClick={handleEndChat}>
                  End chat
                </button>
              </div>
            )}
          </div>
        </header>

        {(connectionPhase === 'top' || connectionPhase === 'failed') && (
          <ConnectionStatus
            phase={connectionPhase}
            onRetry={handleRetryConnection}
          />
        )}

        {connectionPhase === 'mid' && (
          <ConnectionStatus phase="mid" onRetry={handleRetryConnection} />
        )}

        <ReminderPrompt
          visible={showReminderPrompt}
          busy={reminderBusy}
          error={reminderError}
          onEnable={handleEnableReminders}
          onDismiss={handleDismissReminderPrompt}
        />

        {pushNudge ? (
          <div className="chat-push-nudge" role="status">
            {pushNudge.title ? (
              <strong className="chat-push-nudge-title">{pushNudge.title}</strong>
            ) : null}
            {pushNudge.body ? (
              <span className="chat-push-nudge-body">{pushNudge.body}</span>
            ) : null}
          </div>
        ) : null}

        <div
          ref={threadRef}
          className={`chat-thread${isScrolling ? ' is-scrolling' : ''}`}
        >
          {isLoadingHistory && messages.length === 0 ? (
            <ChatThreadSkeleton />
          ) : messages.length === 0 ? (
            <div className="chat-empty chat-anim-fade">
              <ChannelAvatar src={profilePictureUrl} />
              <div className="chat-empty-title">
                {webChatChannel?.channel_name || 'Start the conversation'}
              </div>
              <p className="chat-empty-copy">
                Say hello to begin. Your messages will show up here.
              </p>
            </div>
          ) : (
            <Virtuoso
              ref={virtuosoRef}
              style={{ height: '100%' }}
              data={timelineItems}
              alignToBottom
              initialTopMostItemIndex={Math.max(timelineItems.length - 1, 0)}
              atBottomThreshold={AT_BOTTOM_THRESHOLD_PX}
              isScrolling={(scrolling) => {
                isScrollingRef.current = scrolling;
                setIsScrolling(scrolling);
              }}
              atBottomStateChange={(atBottom) => {
                atBottomRef.current = atBottom;

                if (programmaticScrollRef.current) {
                  if (atBottom) setUnreadWhileAway(0);
                  return;
                }

                if (atBottom) {
                  stickToBottomRef.current = true;
                  setShowScrollButton(false);
                  setUnreadWhileAway(0);
                  return;
                }

                // Left the bottom. Prefer releasing while the user is actively
                // scrolling; wheel/touch handlers also release for the race where
                // isScrolling has already flipped false.
                if (isScrollingRef.current) {
                  stickToBottomRef.current = false;
                  setShowScrollButton(true);
                } else if (!stickToBottomRef.current) {
                  setShowScrollButton(true);
                }
              }}
              followOutput={() => (stickToBottomRef.current ? 'auto' : false)}
              increaseViewportBy={240}
              itemContent={renderTimelineItem}
              components={virtuosoComponents}
            />
          )}
        </div>

        {showScrollButton && (
          <button
            type="button"
            className="chat-jump"
            onClick={handleScrollToBottom}
            aria-label="Scroll to latest messages"
          >
            <ArrowDownIcon className="h-5 w-5" />
            {unreadWhileAway > 0 && (
              <span className="chat-jump-count">{unreadWhileAway}</span>
            )}
          </button>
        )}

        {showWaitingBubble && (
          <div className="chat-typing-dock" aria-live="polite">
            <TypingBubble avatarUrl={profilePictureUrl} />
          </div>
        )}

        {/* Answer-first: when the bot offers choices they are the primary
            input and the composer steps aside. With no choices on offer the
            composer is the only thing to show. */}
        <div className="chat-bottom" ref={bottomRef}>
          {showAnswerDeck ? (
            <AnswerDeck
              replies={answerOptions}
              onSelect={handleQuickReplyClick}
              onTypeInstead={handleTypeInstead}
              disabled={!isSocketReady}
            />
          ) : (
            <ChatComposer
              value={inputMessage}
              onChange={(value) => {
                setInputMessage(value);
                if (sendError) setSendError('');
              }}
              onSend={handleSendMessage}
              disabled={!isSocketReady}
              placeholder={
                connectionPhase === 'ready'
                  ? 'Type a message'
                  : connectionPhase === 'failed'
                    ? 'Reconnect to send messages'
                    : 'Reconnecting…'
              }
              maxLength={MAX_MESSAGE_LENGTH}
              sendError={sendError}
              inputRef={inputRef}
              onBackToAnswers={
                answerOptions.length ? () => setComposerMode('deck') : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Chat;
